import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Briefcase, FileText, Upload, MessageCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // WhatsApp Image Upload States (for modal compatibility)
  const [showWhatsappImageModal, setShowWhatsappImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSendingImage, setIsSendingImage] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_rate: '',
    hsn_sac_code: '',
    gst_rate: ''
  });
  
  useEffect(() => {
    fetchServices();
  }, []);
  
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        default_rate: parseFloat(formData.default_rate),
        gst_rate: parseFloat(formData.gst_rate || 0)
      };
      
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, data);
      } else {
        await axios.post(`${API}/services`, data);
      }
      
      fetchServices();
      setShowForm(false);
      setEditingService(null);
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };
  
  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      default_rate: service.default_rate.toString(),
      hsn_sac_code: service.hsn_sac_code || '',
      gst_rate: service.gst_rate.toString()
    });
    setShowForm(true);
  };
  
  const handleDelete = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await axios.delete(`${API}/services/${serviceId}`);
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  // WhatsApp image functionality (placeholder for Services compatibility)
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageViaWhatsApp = async () => {
    alert('This feature is available in the Invoices section');
    setShowWhatsappImageModal(false);
    setImageFile(null);
    setImagePreview(null);
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_rate: '',
      hsn_sac_code: '',
      gst_rate: ''
    });
  };
  
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.hsn_sac_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Service Management
              </h1>
              <p className="text-gray-600 text-lg">Configure your professional services with GST rates and HSN/SAC codes</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Total Services</div>
                <div className="text-2xl font-bold">{services.length}</div>
              </div>
              <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-service-btn" 
                    onClick={() => { resetForm(); setEditingService(null); }}
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Service
                  </Button>
                </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  data-testid="service-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  data-testid="service-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="default_rate">Default Rate (₹) *</Label>
                <Input
                  id="default_rate"
                  type="number"
                  step="0.01"
                  data-testid="service-rate-input"
                  value={formData.default_rate}
                  onChange={(e) => setFormData({...formData, default_rate: e.target.value})}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hsn_sac_code">HSN/SAC Code</Label>
                  <Input
                    id="hsn_sac_code"
                    data-testid="hsn-sac-input"
                    value={formData.hsn_sac_code}
                    onChange={(e) => setFormData({...formData, hsn_sac_code: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="gst_rate">GST Rate (%)</Label>
                  <Input
                    id="gst_rate"
                    type="number"
                    step="0.01"
                    data-testid="gst-rate-input"
                    value={formData.gst_rate}
                    onChange={(e) => setFormData({...formData, gst_rate: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-service-btn">
                  {editingService ? 'Update' : 'Save'} Service
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Search Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="relative">
              <Input
                placeholder="Search services by name, description, or HSN/SAC code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg border-orange-200 focus:border-orange-400 focus:ring-orange-400 rounded-xl"
              />
              <FileText className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        {/* Services Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                  <div className="h-20 bg-gray-200 rounded-xl animate-pulse"></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-10 bg-gray-200 rounded flex-1 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
            <Card key={service.id} className="bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{service.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {service.description || 'No description provided'}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Price */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Default Rate</span>
                      <span className="text-2xl font-bold text-green-600">₹{service.default_rate.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* GST & HSN/SAC Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">GST Rate</div>
                      <div className="text-lg font-bold text-blue-600">{service.gst_rate}%</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">HSN/SAC</div>
                      <div className="text-sm font-bold text-purple-600 font-mono">
                        {service.hsn_sac_code || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Tax Information */}
                  {service.gst_rate > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Tax Amount</div>
                      <div className="text-sm font-semibold text-orange-600">
                        ₹{((service.default_rate * service.gst_rate) / 100).toLocaleString()} 
                        <span className="text-xs ml-1">({service.gst_rate}% GST)</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleEdit(service)}
                    className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(service.id)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredServices.length === 0 && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <CardContent className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-12 h-12 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No services found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your professional services'}
              </p>
              <Button 
                onClick={() => { resetForm(); setEditingService(null); setShowForm(true); }}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-8 py-3 rounded-xl"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Service
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats - Enhanced */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide mb-2">Total Services</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : services.length}</div>
              <div className="text-blue-100 text-sm opacity-90">Active services</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-green-100 text-sm font-semibold uppercase tracking-wide mb-2">Avg. Service Rate</p>
              <div className="text-4xl font-black mb-2">
                {loading ? '...' : `₹${services.length > 0 ? Math.round(services.reduce((acc, s) => acc + s.default_rate, 0) / services.length).toLocaleString() : 0}`}
              </div>
              <div className="text-green-100 text-sm opacity-90">Average pricing</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-purple-100 text-sm font-semibold uppercase tracking-wide mb-2">GST Applicable</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : services.filter(s => s.gst_rate > 0).length}</div>
              <div className="text-purple-100 text-sm opacity-90">With GST</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-orange-100 text-sm font-semibold uppercase tracking-wide mb-2">Avg. GST Rate</p>
              <div className="text-4xl font-black mb-2">
                {loading ? '...' : `${services.length > 0 ? Math.round(services.reduce((acc, s) => acc + s.gst_rate, 0) / services.length) : 0}%`}
              </div>
              <div className="text-orange-100 text-sm opacity-90">Average tax</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WhatsApp Image Upload Modal */}
      {showWhatsappImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Send Image via WhatsApp</h3>
                <button
                  onClick={() => {
                    setShowWhatsappImageModal(false);
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>To:</strong> {invoice.client_name}</p>
                  <p><strong>Mobile:</strong> {invoice.client_mobile}</p>
                  <p><strong>Invoice:</strong> {invoice.invoice_number}</p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={imagePreview} 
                        alt="Selected" 
                        className="max-w-full max-h-48 mx-auto rounded"
                      />
                      <div className="text-sm text-gray-600">
                        {imageFile?.name} ({(imageFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                      <button
                        onClick={() => document.getElementById('whatsapp-image-input').click()}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Choose different image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-gray-600 mb-2">Select an image to send with the invoice</p>
                        <button
                          onClick={() => document.getElementById('whatsapp-image-input').click()}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Select Image
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Max size: 5MB. Supported: JPG, PNG, GIF</p>
                    </div>
                  )}
                  
                  <input
                    id="whatsapp-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowWhatsappImageModal(false);
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={isSendingImage}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendImageViaWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!imageFile || isSendingImage}
                  >
                    {isSendingImage ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Send via WhatsApp
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
 
    </div>
  );
};

export default Services;
